import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent } from '@ohif/ui-next';
import { X } from 'lucide-react';

import { ToggleSwitch } from '../common';
import {
  fetchAndStoreModalities,
  getModalities,
  mapApiModalitiesToOptions,
  modalityCodesMatch,
  type Modality,
} from '../../utils/modalityUtils';
import {
  createWorklistPriorityRule,
  updateWorklistPriorityRule,
  type WorklistCriterionType,
  type WorklistPriorityRule,
} from '../../services/worklistPriorityRuleService';
import {
  getRadiologyTestNames,
  type RadiologyTestNameOption,
} from '../../services/radiologyTestService';
import {
  CRITERION_TYPE_OPTIONS,
  ORDER_PRIORITY_OPTIONS,
  TAT_STATUS_OPTIONS,
  resolveModalityOptionValue,
} from '../../utils/worklistPriorityUtils';
import { ICON_STROKE_WIDTH, Z_INDEX_CHANGE_PASSWORD_MODAL } from '../../constants';
import { parseStoredTestNames, serializeStoredTestNames } from '../../utils/testNameListUtils';
import RadiologyTestNamePicker from './RadiologyTestNamePicker';
import './AddRoutingRuleModal.css';
import './AddWorklistPriorityRuleModal.css';

interface AddWorklistPriorityRuleFormValues {
  name: string;
  criterionType: WorklistCriterionType;
  criterionValue: string;
  selectedTestNames: string[];
  modalityCode: string;
  active: boolean;
}

interface AddWorklistPriorityRuleModalProps {
  isOpen: boolean;
  facilityId: number;
  editingRule?: WorklistPriorityRule | null;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const DEFAULT_FORM: AddWorklistPriorityRuleFormValues = {
  name: '',
  criterionType: 'order_priority',
  criterionValue: 'URGENT',
  selectedTestNames: [],
  modalityCode: '',
  active: true,
};

function resolveModalityId(modalities: Modality[], code: string): number | null {
  if (!code.trim()) {
    return null;
  }
  const match = modalities.find(item => modalityCodesMatch(code, item.name));
  return match?.id ?? null;
}

function ruleToForm(rule: WorklistPriorityRule): AddWorklistPriorityRuleFormValues {
  const criterionValue =
    rule.criterionType === 'order_priority'
      ? (rule.criterionValue?.toUpperCase() ?? '')
      : rule.criterionValue;

  return {
    name: rule.name,
    criterionType: rule.criterionType,
    criterionValue,
    selectedTestNames:
      rule.criterionType === 'subspecialty' ? parseStoredTestNames(rule.criterionValue) : [],
    modalityCode: resolveModalityOptionValue(rule),
    active: rule.isActive,
  };
}

function applyWorklistPriorityRuleModalZIndex(): void {
  const content = document.querySelector(
    '.add-worklist-priority-rule-dialog-content'
  ) as HTMLElement | null;
  if (!content) {
    return;
  }
  content.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
  const overlay = content.previousElementSibling as HTMLElement | null;
  if (overlay) {
    overlay.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
  }
}

function resolveCriterionValueForSubmit(form: AddWorklistPriorityRuleFormValues): string {
  if (form.criterionType === 'modality') {
    return form.modalityCode.trim();
  }
  if (form.criterionType === 'order_priority') {
    return form.criterionValue.trim().toUpperCase();
  }
  if (form.criterionType === 'subspecialty') {
    return serializeStoredTestNames(form.selectedTestNames);
  }
  return form.criterionValue.trim();
}

function buildCriterionTypeFormReset(
  prev: AddWorklistPriorityRuleFormValues,
  criterionType: WorklistCriterionType,
  firstModalityCode: string
): AddWorklistPriorityRuleFormValues {
  const next: AddWorklistPriorityRuleFormValues = {
    ...prev,
    criterionType,
    criterionValue: '',
    selectedTestNames: [],
    modalityCode: '',
  };

  if (criterionType === 'order_priority') {
    next.criterionValue = 'URGENT';
    return next;
  }
  if (criterionType === 'tat_status') {
    next.criterionValue = 'near_breach';
    return next;
  }
  if (criterionType === 'modality') {
    next.modalityCode = firstModalityCode;
    next.criterionValue = firstModalityCode;
    return next;
  }
  if (criterionType === 'subspecialty') {
    next.selectedTestNames = [];
  }
  return next;
}

function canSubmitWorklistPriorityForm(
  form: AddWorklistPriorityRuleFormValues,
  loadingModalities: boolean,
  loadingRadiologyTests: boolean,
  resolvedModalityId: number | null
): boolean {
  if (!form.name.trim() || loadingModalities) {
    return false;
  }
  if (form.criterionType === 'modality') {
    return Boolean(form.modalityCode.trim()) && resolvedModalityId != null;
  }
  if (form.criterionType === 'subspecialty') {
    return form.selectedTestNames.length > 0 && !loadingRadiologyTests;
  }
  return Boolean(form.criterionValue.trim());
}

function WorklistCriterionValueField({
  form,
  modalityOptions,
  loadingModalities,
  radiologyTests,
  loadingRadiologyTests,
  onClearError,
  onFormChange,
}: Readonly<{
  form: AddWorklistPriorityRuleFormValues;
  modalityOptions: ReturnType<typeof mapApiModalitiesToOptions>;
  loadingModalities: boolean;
  radiologyTests: RadiologyTestNameOption[];
  loadingRadiologyTests: boolean;
  onClearError: () => void;
  onFormChange: (
    updater: (prev: AddWorklistPriorityRuleFormValues) => AddWorklistPriorityRuleFormValues
  ) => void;
}>) {
  if (form.criterionType === 'modality') {
    return (
      <select
        id="worklist-priority-criterion-value"
        className="add-routing-rule-select worklist-priority-select"
        value={form.modalityCode}
        disabled={loadingModalities || modalityOptions.length === 0}
        onChange={event => {
          onClearError();
          onFormChange(prev => ({
            ...prev,
            modalityCode: event.target.value,
            criterionValue: event.target.value,
          }));
        }}
      >
        {modalityOptions.length === 0 ? (
          <option value="">
            {loadingModalities ? 'Loading modalities…' : 'No modalities available'}
          </option>
        ) : (
          modalityOptions.map(option => (
            <option
              key={option.id}
              value={option.value}
            >
              {option.label}
            </option>
          ))
        )}
      </select>
    );
  }

  if (form.criterionType === 'order_priority') {
    return (
      <select
        id="worklist-priority-criterion-value"
        className="add-routing-rule-select worklist-priority-select"
        value={form.criterionValue}
        onChange={event => onFormChange(prev => ({ ...prev, criterionValue: event.target.value }))}
      >
        {ORDER_PRIORITY_OPTIONS.map(option => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (form.criterionType === 'subspecialty') {
    return (
      <>
        <RadiologyTestNamePicker
          id="worklist-priority-criterion-value"
          selectedTestNames={form.selectedTestNames}
          onChange={selectedTestNames => onFormChange(prev => ({ ...prev, selectedTestNames }))}
          tests={radiologyTests}
          loading={loadingRadiologyTests}
          placeholder="Select one or more test names…"
          emptyMessage={
            radiologyTests.length === 0 && !loadingRadiologyTests
              ? 'No tests available'
              : 'No tests found'
          }
        />
        <p className="add-routing-rule-hint">
          Select multiple test names. Use ✕ on a chip or click a selected test again to remove it.
        </p>
      </>
    );
  }

  if (form.criterionType === 'tat_status') {
    return (
      <select
        id="worklist-priority-criterion-value"
        className="add-routing-rule-select worklist-priority-select"
        value={form.criterionValue}
        onChange={event => onFormChange(prev => ({ ...prev, criterionValue: event.target.value }))}
      >
        {TAT_STATUS_OPTIONS.map(option => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      id="worklist-priority-criterion-value"
      type="text"
      className="add-routing-rule-input"
      placeholder="e.g. Stroke, Trauma, Critical Case"
      value={form.criterionValue}
      onChange={event => onFormChange(prev => ({ ...prev, criterionValue: event.target.value }))}
    />
  );
}

export default function AddWorklistPriorityRuleModal({
  isOpen,
  facilityId,
  editingRule,
  onClose,
  onSubmit,
}: Readonly<AddWorklistPriorityRuleModalProps>) {
  const [form, setForm] = useState<AddWorklistPriorityRuleFormValues>(DEFAULT_FORM);
  const [modalities, setModalities] = useState<Modality[]>([]);
  const [loadingModalities, setLoadingModalities] = useState(false);
  const [radiologyTests, setRadiologyTests] = useState<RadiologyTestNameOption[]>([]);
  const [loadingRadiologyTests, setLoadingRadiologyTests] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = Boolean(editingRule?.id);

  const modalityOptions = useMemo(() => mapApiModalitiesToOptions(modalities), [modalities]);

  const loadModalities = useCallback(async () => {
    setLoadingModalities(true);
    try {
      let stored = getModalities();
      if (!stored?.length) {
        await fetchAndStoreModalities();
        stored = getModalities();
      }
      setModalities(stored ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load modalities');
    } finally {
      setLoadingModalities(false);
    }
  }, []);

  const loadRadiologyTests = useCallback(async () => {
    setLoadingRadiologyTests(true);
    try {
      const result = await getRadiologyTestNames(facilityId, { active_only: true });
      if (!result.success) {
        setError(result.error || 'Failed to load radiology tests');
        setRadiologyTests([]);
        return;
      }
      setRadiologyTests(result.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load radiology tests');
      setRadiologyTests([]);
    } finally {
      setLoadingRadiologyTests(false);
    }
  }, [facilityId]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }
    const rafId = requestAnimationFrame(() => {
      applyWorklistPriorityRuleModalZIndex();
    });
    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setError(null);
    setForm(editingRule ? ruleToForm(editingRule) : DEFAULT_FORM);
    void loadModalities();
    void loadRadiologyTests();
  }, [isOpen, editingRule, loadModalities, loadRadiologyTests]);

  useEffect(() => {
    if (
      !isOpen ||
      form.criterionType !== 'modality' ||
      loadingModalities ||
      !modalityOptions.length
    ) {
      return;
    }

    const hasMatch = modalityOptions.some(option =>
      modalityCodesMatch(form.modalityCode, option.value)
    );

    if (!hasMatch) {
      const firstOption = modalityOptions[0];
      setForm(prev => ({
        ...prev,
        modalityCode: firstOption.value,
        criterionValue: firstOption.value,
      }));
    }
  }, [isOpen, form.criterionType, form.modalityCode, loadingModalities, modalityOptions]);

  const resolvedModalityId = useMemo(() => {
    if (form.criterionType !== 'modality' || !form.modalityCode.trim()) {
      return null;
    }
    return resolveModalityId(modalities, form.modalityCode);
  }, [form.criterionType, form.modalityCode, modalities]);

  const canSubmit = useMemo(
    () =>
      canSubmitWorklistPriorityForm(
        form,
        loadingModalities,
        loadingRadiologyTests,
        resolvedModalityId
      ),
    [form, loadingModalities, loadingRadiologyTests, resolvedModalityId]
  );

  const handleCriterionTypeChange = (criterionType: WorklistCriterionType) => {
    setError(null);
    const firstModalityCode = modalityOptions[0]?.value ?? '';
    setForm(prev => buildCriterionTypeFormReset(prev, criterionType, firstModalityCode));
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!canSubmit) {
      return;
    }

    const modalityId =
      form.criterionType === 'modality' ? resolveModalityId(modalities, form.modalityCode) : null;

    if (form.criterionType === 'modality' && modalityId == null) {
      setError(
        `Could not match modality "${form.modalityCode}" to your facility catalog. Refresh the page and try again.`
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const payload = {
      facilityId,
      name: form.name.trim(),
      criterionType: form.criterionType,
      criterionValue: resolveCriterionValueForSubmit(form),
      modalityId: form.criterionType === 'modality' ? modalityId : null,
      isActive: form.active,
    };

    const result = isEditing
      ? await updateWorklistPriorityRule(editingRule!.id, payload)
      : await createWorklistPriorityRule(payload);

    setIsSubmitting(false);

    if (!result.success) {
      setError(result.error || 'Failed to save worklist priority rule');
      return;
    }

    await onSubmit();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => !open && !isSubmitting && onClose()}
    >
      <DialogContent
        unstyled
        showCloseButton={false}
        className="add-worklist-priority-rule-dialog-content add-routing-rule-dialog-content"
      >
        <button
          type="button"
          className="add-routing-rule-close-btn"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close dialog"
        >
          <X
            strokeWidth={ICON_STROKE_WIDTH}
            size={20}
          />
        </button>

        <header className="add-routing-rule-header">
          <h2 className="add-routing-rule-title">
            {isEditing ? 'Edit prioritization rule' : 'Add prioritization rule'}
          </h2>
          <p className="add-routing-rule-subtitle">
            Studies are ordered by the first matching rule in the list. Drag rows on the main table
            to set rule precedence.
          </p>
        </header>

        <form
          className="add-routing-rule-form"
          onSubmit={handleSubmit}
        >
          <div className="add-routing-rule-form-body">
            {error ? (
              <p
                className="add-routing-rule-error"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div className="add-routing-rule-field">
              <label
                className="add-routing-rule-label"
                htmlFor="worklist-priority-rule-name"
              >
                Rule name
              </label>
              <input
                id="worklist-priority-rule-name"
                type="text"
                className="add-routing-rule-input"
                placeholder="e.g. Stroke flag — critical boost"
                value={form.name}
                onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
              />
            </div>

            <div className="add-routing-rule-grid add-routing-rule-grid--two worklist-priority-criterion-row">
              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="worklist-priority-criterion-type"
                >
                  Criterion type
                </label>
                <select
                  id="worklist-priority-criterion-type"
                  className="add-routing-rule-select worklist-priority-select"
                  value={form.criterionType}
                  onChange={event =>
                    handleCriterionTypeChange(event.target.value as WorklistCriterionType)
                  }
                >
                  {CRITERION_TYPE_OPTIONS.map(option => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="worklist-priority-criterion-value"
                >
                  Criterion value
                </label>
                <WorklistCriterionValueField
                  form={form}
                  modalityOptions={modalityOptions}
                  loadingModalities={loadingModalities}
                  radiologyTests={radiologyTests}
                  loadingRadiologyTests={loadingRadiologyTests}
                  onClearError={() => setError(null)}
                  onFormChange={setForm}
                />
              </div>
            </div>
          </div>

          <footer className="add-routing-rule-footer">
            <label className="add-routing-rule-active">
              <span>Active</span>
              <ToggleSwitch
                checked={form.active}
                onChange={checked => setForm(prev => ({ ...prev, active: checked }))}
              />
            </label>

            <div className="add-routing-rule-footer-actions">
              <button
                type="button"
                className="add-routing-rule-btn secondary"
                disabled={isSubmitting}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-routing-rule-btn primary"
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? 'Saving…' : isEditing ? 'Save changes' : 'Add rule'}
              </button>
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}
